import { useState, useEffect, useRef } from 'react';

interface UseSpeechRecognitionOptions {
	onResult?: (transcript: string, isFinal: boolean) => void;
	onError?: (error: string) => void;
	onEnd?: (finalTranscript: string) => void;
	language?: string;
	continuous?: boolean;
	interimResults?: boolean;
}

export const useSpeechRecognition = (options: UseSpeechRecognitionOptions = {}) => {
	const { onResult, onError, onEnd, language = 'en-US', continuous = false, interimResults = true } = options;

	const [isListening, setIsListening] = useState(false);
	const [isSupported, setIsSupported] = useState(false);
	const recognizerRef = useRef<SpeechRecognition | null>(null);
	const finalTranscriptRef = useRef<string>('');
	const callbacksRef = useRef({ onResult, onError, onEnd });

	// Update callbacks ref when they change
	useEffect(() => {
		callbacksRef.current = { onResult, onError, onEnd };
	}, [onResult, onError, onEnd]);

	// Initialize recognizer once
	useEffect(() => {
		const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

		if (!SpeechRecognition) {
			setIsSupported(false);
			return;
		}

		setIsSupported(true);
		const recognizer = new SpeechRecognition();
		recognizerRef.current = recognizer;

		recognizer.addEventListener('result', (event: SpeechRecognitionEvent) => {
			let interimTranscript = '';
			let finalTranscript = '';

			for (let i = event.resultIndex; i < event.results.length; i++) {
				const transcript = event.results[i][0].transcript;
				if (event.results[i].isFinal) {
					finalTranscript += transcript;
				} else {
					interimTranscript += transcript;
				}
			}

			if (finalTranscript) {
				finalTranscriptRef.current += finalTranscript;
				callbacksRef.current.onResult?.(finalTranscriptRef.current, true);
			} else if (interimTranscript) {
				callbacksRef.current.onResult?.(finalTranscriptRef.current + interimTranscript, false);
			}
		});

		recognizer.addEventListener('error', (event: SpeechRecognitionErrorEvent) => {
			const errorMessage = event.error || 'Unknown recognition error';
			callbacksRef.current.onError?.(errorMessage);
			setIsListening(false);
		});

		recognizer.addEventListener('end', () => {
			setIsListening(false);
			const finalTranscript = finalTranscriptRef.current;
			callbacksRef.current.onEnd?.(finalTranscript);
		});

		return () => {
			if (recognizerRef.current) {
				try {
					recognizerRef.current.stop();
				} catch {
					// Ignore errors when stopping
				}
			}
		};
	}, []);

	// Update language and settings when they change
	useEffect(() => {
		if (recognizerRef.current) {
			recognizerRef.current.lang = language;
			recognizerRef.current.continuous = continuous;
			recognizerRef.current.interimResults = interimResults;
		}
	}, [language, continuous, interimResults]);

	const startListening = () => {
		if (!isSupported || !recognizerRef.current || isListening) {
			return;
		}

		try {
			finalTranscriptRef.current = '';
			recognizerRef.current.start();
			setIsListening(true);
		} catch (error: any) {
			onError?.(error.message || 'Failed to start recognition');
		}
	};

	const stopListening = () => {
		if (!recognizerRef.current || !isListening) {
			return;
		}

		try {
			recognizerRef.current.stop();
			setIsListening(false);
		} catch (error: any) {
			onError?.(error.message || 'Failed to stop recognition');
		}
	};

	return {
		isListening,
		isSupported,
		startListening,
		stopListening
	};
};
